import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, Zap, History, Clock } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { toast } from "sonner";

function NumField({ label, value, onChange, testId }) {
  return (
    <label className="flex-1 min-w-[70px]">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-[#64748B] mb-0.5">{label}</span>
      <Input
        type="number"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="h-8 text-sm"
        data-testid={testId}
      />
    </label>
  );
}

export default function ExpressAdminPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [radius, setRadius] = useState(5);
  const [timeout_, setTimeout_] = useState(30);
  const [surge, setSurge] = useState(1);
  // NEW: forfaits, overtime, trip zones
  const [forfaits, setForfaits] = useState([]);
  const [overtime, setOvertime] = useState({ first_15: 1000, next_15: 900, beyond_15: 800 });
  const [zones, setZones] = useState([]);
  // NEW (iter 69): top-level Express service toggles.
  const [options, setOptions] = useState({ trip_simple_enabled: true, forfait_enabled: true });

  const load = () => {
    setLoading(true);
    api.get("/admin/express/config").then(({ data }) => {
      setTypes(data.vehicle_types || []);
      setRadius(data.match_radius_km ?? 5);
      setTimeout_(data.driver_accept_timeout_seconds ?? 30);
      setSurge(data.surge_multiplier ?? 1);
      setForfaits(data.forfaits || []);
      setOvertime(data.overtime || { first_15: 1000, next_15: 900, beyond_15: 800 });
      setZones(data.trip_zones || []);
      setOptions(data.options || { trip_simple_enabled: true, forfait_enabled: true });
    }).catch(() => toast.error("Impossible de charger la config")).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateType = (i, patch) => setTypes((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const removeType = (i) => setTypes((prev) => prev.filter((_, idx) => idx !== i));
  const addType = () => setTypes((prev) => [...prev, { id: `type-${prev.length + 1}`, label: "Nouveau type", emoji: "🚗", description: "", base: 500, per_km: 300, per_min: 25, min_fare: 1000, max_passengers: 4, active: true }]);

  const save = async () => {
    setSaving(true);
    try {
      // Safety net: refuse to save if both service tabs are OFF. Backend
      // already enforces this but we surface a friendly message to the
      // admin so they realise their change is being blocked.
      if (!options.trip_simple_enabled && !options.forfait_enabled) {
        toast.error("Au moins une option Express doit rester activée.");
        setSaving(false);
        return;
      }
      await api.put("/admin/express/config", {
        vehicle_types: types,
        match_radius_km: radius,
        driver_accept_timeout_seconds: timeout_,
        surge_multiplier: surge,
        forfaits,
        overtime,
        trip_zones: zones,
        options,
      });
      toast.success("Configuration enregistrée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-express-panel">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-5 h-5 text-[#FACC15]" />
        <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Trigo Express — Tarification</h2>
      </div>

      {/* Iter 69 — Service options toggle. Lets the admin turn "Trajet
          simple" or "Forfait temps" ON/OFF for all passengers in one click.
          Iter 70 — Optional automatic scheduling window per service.
          Backend refuses to save if both master toggles are OFF (safety net). */}
      <div className="bg-gradient-to-br from-[#FEF9C3] via-white to-[#F5F7FA] p-4 rounded-2xl border border-[#FACC15]/40" data-testid="express-options-panel">
        <p className="text-[11px] uppercase tracking-widest font-extrabold text-[#854D0E] mb-2">
          Options du service Express
        </p>
        <p className="text-xs text-[#5B4A0F] mb-3">
          Active ou désactive les modes disponibles pour les clients. Au moins une option doit rester active. Programme une plage horaire pour désactiver automatiquement un service.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div className="min-w-0">
                <p className="font-bold text-sm text-[#0A2540]">🛣️ Trajet simple</p>
                <p className="text-[11px] text-[#64748B] leading-tight">Course d'un point A à un point B (tarif par zone).</p>
              </div>
              <Switch
                checked={!!options.trip_simple_enabled}
                onCheckedChange={(v) => setOptions((o) => ({ ...o, trip_simple_enabled: !!v }))}
                data-testid="express-option-trip-simple"
              />
            </label>
            <ScheduleEditor
              service="trip_simple"
              schedule={(options.schedules && options.schedules.trip_simple) || {}}
              onChange={(s) => setOptions((o) => ({ ...o, schedules: { ...(o.schedules || {}), trip_simple: s } }))}
            />
          </div>
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div className="min-w-0">
                <p className="font-bold text-sm text-[#0A2540]">⏱️ Forfait temps</p>
                <p className="text-[11px] text-[#64748B] leading-tight">Location du chauffeur pour une durée fixe.</p>
              </div>
              <Switch
                checked={!!options.forfait_enabled}
                onCheckedChange={(v) => setOptions((o) => ({ ...o, forfait_enabled: !!v }))}
                data-testid="express-option-forfait"
              />
            </label>
            <ScheduleEditor
              service="forfait"
              schedule={(options.schedules && options.schedules.forfait) || {}}
              onChange={(s) => setOptions((o) => ({ ...o, schedules: { ...(o.schedules || {}), forfait: s } }))}
            />
          </div>
        </div>
      </div>

      {/* Global settings */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-4 rounded-xl border border-[#CBD5E1]">
        <NumField label="Rayon de recherche (km)" value={radius} onChange={setRadius} testId="express-cfg-radius" />
        <NumField label="Timeout acceptation (s)" value={timeout_} onChange={setTimeout_} testId="express-cfg-timeout" />
        <NumField label="Multiplicateur surge" value={surge} onChange={setSurge} testId="express-cfg-surge" />
      </div>

      {/* Vehicle types */}
      <div className="space-y-3">
        {types.map((t, i) => (
          <div key={i} className="bg-white p-4 rounded-xl border border-[#CBD5E1] space-y-3" data-testid={`express-type-row-${t.id}`}>
            <div className="flex items-center gap-3">
              <Input value={t.emoji || ""} onChange={(e) => updateType(i, { emoji: e.target.value })} className="w-12 h-9 text-center text-lg" data-testid={`express-type-emoji-${t.id}`} />
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input value={t.label} onChange={(e) => updateType(i, { label: e.target.value })} placeholder="Nom" className="h-9" data-testid={`express-type-label-${t.id}`} />
                <Input value={t.id} onChange={(e) => updateType(i, { id: e.target.value })} placeholder="id (moto, berline, …)" className="h-9 font-mono text-xs" data-testid={`express-type-id-${t.id}`} />
              </div>
              <Switch checked={!!t.active} onCheckedChange={(v) => updateType(i, { active: v })} data-testid={`express-type-active-${t.id}`} />
              <Button size="icon" variant="ghost" onClick={() => removeType(i)} className="text-[#EF4444]" data-testid={`express-type-remove-${t.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <Input value={t.description || ""} onChange={(e) => updateType(i, { description: e.target.value })} placeholder="Description courte (ex : 1 passager · le plus rapide)" className="h-9 text-sm" />
            <div className="flex items-end gap-2 flex-wrap">
              <NumField label="Base (F)" value={t.base} onChange={(v) => updateType(i, { base: v })} testId={`express-type-base-${t.id}`} />
              <NumField label="Par km" value={t.per_km} onChange={(v) => updateType(i, { per_km: v })} testId={`express-type-perkm-${t.id}`} />
              <NumField label="Par min" value={t.per_min} onChange={(v) => updateType(i, { per_min: v })} testId={`express-type-permin-${t.id}`} />
              <NumField label="Min. course" value={t.min_fare} onChange={(v) => updateType(i, { min_fare: v })} testId={`express-type-minfare-${t.id}`} />
              <NumField label="Max pass." value={t.max_passengers} onChange={(v) => updateType(i, { max_passengers: v })} testId={`express-type-maxpass-${t.id}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 pt-2">
        <Button variant="outline" onClick={addType} data-testid="express-add-type-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un type</Button>
        <Button onClick={save} disabled={saving} className="bg-[#0A2540] text-white" data-testid="express-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer
        </Button>
      </div>

      {/* === FORFAITS TEMPS === */}
      <div className="mt-8 space-y-3" data-testid="express-forfaits-section">
        <h3 className="text-base font-bold text-[#0A2540] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          ⏱️ Forfaits temps
        </h3>
        {forfaits.map((f, i) => (
          <div key={i} className={`p-3 rounded-xl border ${f.active === false ? "bg-[#F1F5F9] border-[#CBD5E1] opacity-70" : "bg-white border-[#CBD5E1]"}`} data-testid={`express-forfait-row-${f.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input value={f.emoji || ""} onChange={(e) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,emoji:e.target.value}:x))} className="w-12 h-9 text-center text-lg" />
              <Input value={f.label} onChange={(e) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,label:e.target.value}:x))} placeholder="Nom" className="flex-1 h-9" />
              <Input value={f.id} onChange={(e) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,id:e.target.value}:x))} placeholder="id" className="w-28 h-9 font-mono text-xs" />
              <Switch checked={f.active !== false} onCheckedChange={(v) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,active:!!v}:x))} data-testid={`express-forfait-active-${f.id}`} />
              <Button size="icon" variant="ghost" onClick={() => setForfaits(p => p.filter((_,idx)=>idx!==i))} className="text-[#EF4444]"><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="flex gap-2 items-end">
              <NumField label="Durée (min)" value={f.duration_min} onChange={(v) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,duration_min:v}:x))} />
              <NumField label="Prix (F)" value={f.price} onChange={(v) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,price:v}:x))} />
              <Input value={f.description || ""} onChange={(e) => setForfaits(p => p.map((x,idx)=>idx===i?{...x,description:e.target.value}:x))} placeholder="Description" className="flex-1 h-9 text-sm" />
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={() => setForfaits(p => [...p, { id:`forfait-${p.length+1}`, label:"Nouveau", emoji:"⏱️", duration_min:60, price:5000, description:"", active:true }])} data-testid="express-add-forfait-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un forfait</Button>
      </div>

      {/* === DÉPASSEMENT === */}
      <div className="mt-6 bg-[#FEFCE8] p-4 rounded-xl border border-[#FACC15]/40" data-testid="express-overtime-section">
        <h3 className="text-sm font-bold text-[#854D0E] mb-3">💡 Tarifs de dépassement (par tranches de 15 min)</h3>
        <div className="grid grid-cols-3 gap-3">
          <NumField label="0 - 15 min (F)"  value={overtime.first_15}  onChange={(v) => setOvertime({...overtime, first_15: v})} testId="ot-first15" />
          <NumField label="15 - 30 min (F)" value={overtime.next_15}   onChange={(v) => setOvertime({...overtime, next_15: v})} testId="ot-next15" />
          <NumField label="Au-delà / 15min" value={overtime.beyond_15} onChange={(v) => setOvertime({...overtime, beyond_15: v})} testId="ot-beyond15" />
        </div>
      </div>

      {/* === TRAJET SIMPLE / ZONES === */}
      <div className="mt-6 space-y-3" data-testid="express-zones-section">
        <h3 className="text-base font-bold text-[#0A2540] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          🛣️ Trajet simple — Zones tarifaires
        </h3>
        <p className="text-[11px] text-[#64748B]">La zone est auto-détectée selon la distance GPS du trajet. Laissez <code>max_km</code> vide pour la zone catch-all (traversée de ville).</p>
        {zones.map((z, i) => (
          <div key={i} className={`p-3 rounded-xl border flex items-center gap-2 flex-wrap ${z.active === false ? "bg-[#F1F5F9] border-[#CBD5E1] opacity-70" : "bg-white border-[#CBD5E1]"}`} data-testid={`express-zone-row-${z.id}`}>
            <Input value={z.emoji || ""} onChange={(e) => setZones(p => p.map((x,idx)=>idx===i?{...x,emoji:e.target.value}:x))} className="w-12 h-9 text-center text-lg" />
            <Input value={z.label} onChange={(e) => setZones(p => p.map((x,idx)=>idx===i?{...x,label:e.target.value}:x))} placeholder="Nom" className="flex-1 h-9 min-w-[120px]" />
            <Input value={z.id} onChange={(e) => setZones(p => p.map((x,idx)=>idx===i?{...x,id:e.target.value}:x))} placeholder="id" className="w-28 h-9 font-mono text-xs" />
            <NumField label="max_km" value={z.max_km ?? ""} onChange={(v) => setZones(p => p.map((x,idx)=>idx===i?{...x,max_km:(v||v===0)?v:null}:x))} />
            <NumField label="Prix (F)" value={z.price} onChange={(v) => setZones(p => p.map((x,idx)=>idx===i?{...x,price:v}:x))} />
            <Switch checked={z.active !== false} onCheckedChange={(v) => setZones(p => p.map((x,idx)=>idx===i?{...x,active:!!v}:x))} data-testid={`express-zone-active-${z.id}`} />
            <Button size="icon" variant="ghost" onClick={() => setZones(p => p.filter((_,idx)=>idx!==i))} className="text-[#EF4444]"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button variant="outline" onClick={() => setZones(p => [...p, { id:`zone-${p.length+1}`, label:"Nouvelle zone", emoji:"🛣️", max_km:null, price:3000, description:"" }])} data-testid="express-add-zone-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter une zone</Button>
      </div>

      <div className="pt-2 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0A2540] text-white" data-testid="express-save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer toute la config
        </Button>
      </div>

      <OvertimeHistory />
    </div>
  );
}


function OvertimeHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/express/overtime-history").then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="mt-8" data-testid="express-overtime-history">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-5 h-5 text-[#0A2540]" />
        <h3 className="text-base font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Historique des dépassements</h3>
      </div>
      {loading ? (
        <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#64748B] text-center py-6 bg-white rounded-xl border border-dashed border-[#CBD5E1]">
          Aucun dépassement enregistré pour l&apos;instant.
        </p>
      ) : (
        <div className="bg-white rounded-xl border border-[#CBD5E1] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9] text-[#0A2540]">
              <tr>
                <th className="p-2.5 text-left">Date</th>
                <th className="p-2.5 text-left">Passager</th>
                <th className="p-2.5 text-left">Chauffeur</th>
                <th className="p-2.5 text-left">Forfait</th>
                <th className="p-2.5 text-right">Base</th>
                <th className="p-2.5 text-right">Extra</th>
                <th className="p-2.5 text-right">Surcoût</th>
                <th className="p-2.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-t border-[#F1F5F9]" data-testid={`overtime-row-${b.id}`}>
                  <td className="p-2.5 text-xs text-[#64748B]">{b.completed_at ? new Date(b.completed_at).toLocaleString("fr-FR") : "—"}</td>
                  <td className="p-2.5">{b.user_name || "—"}</td>
                  <td className="p-2.5">{b.driver_name || "—"}</td>
                  <td className="p-2.5"><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{b.forfait_label} · {b.forfait_duration_min}min</span></td>
                  <td className="p-2.5 text-right text-[#64748B]">{(b.original_forfait_price || 0).toLocaleString("fr-FR")} F</td>
                  <td className="p-2.5 text-right font-bold text-[#EAB308]">+{b.extra_minutes || 0} min</td>
                  <td className="p-2.5 text-right text-[#EAB308]">+{(b.overtime_fee || 0).toLocaleString("fr-FR")} F</td>
                  <td className="p-2.5 text-right font-extrabold text-[#0A2540]">{(b.total_amount || 0).toLocaleString("fr-FR")} F</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// ScheduleEditor — compact inline editor to auto-disable a service
// during a specific time window on selected weekdays. All values live
// under `options.schedules[service]`. Cross-midnight windows (e.g.
// 22:00 → 05:00) are fully supported.
// ----------------------------------------------------------------
const DAY_LABELS = [
  { i: 0, short: "L" }, { i: 1, short: "M" }, { i: 2, short: "M" },
  { i: 3, short: "J" }, { i: 4, short: "V" }, { i: 5, short: "S" }, { i: 6, short: "D" },
];

function ScheduleEditor({ service, schedule, onChange }) {
  const enabled = !!schedule.enabled;
  const start = schedule.start || "22:00";
  const end   = schedule.end   || "05:00";
  const days  = Array.isArray(schedule.days) && schedule.days.length ? schedule.days : [0, 1, 2, 3, 4, 5, 6];

  const patch = (p) => onChange({ enabled, start, end, days, ...p });
  const toggleDay = (i) => {
    const set = new Set(days);
    if (set.has(i)) set.delete(i); else set.add(i);
    patch({ days: Array.from(set).sort((a, b) => a - b) });
  };

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[#E2E8F0]" data-testid={`schedule-${service}`}>
      <label className="flex items-center justify-between gap-2 cursor-pointer">
        <span className="text-[11px] font-bold text-[#0A2540]">Programmer une désactivation auto</span>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => patch({ enabled: !!v })}
          data-testid={`schedule-${service}-enabled`}
        />
      </label>
      {enabled && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-[#64748B] tracking-wider block mb-1">De</label>
              <input
                type="time"
                value={start}
                onChange={(e) => patch({ start: e.target.value })}
                className="w-full h-9 px-2 rounded-md border border-[#CBD5E1] text-sm font-mono"
                data-testid={`schedule-${service}-start`}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-[#64748B] tracking-wider block mb-1">À</label>
              <input
                type="time"
                value={end}
                onChange={(e) => patch({ end: e.target.value })}
                className="w-full h-9 px-2 rounded-md border border-[#CBD5E1] text-sm font-mono"
                data-testid={`schedule-${service}-end`}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#64748B] tracking-wider block mb-1">Jours</label>
            <div className="flex gap-1">
              {DAY_LABELS.map((d) => {
                const on = days.includes(d.i);
                return (
                  <button
                    key={d.i}
                    type="button"
                    onClick={() => toggleDay(d.i)}
                    className={`w-8 h-8 rounded-full text-[11px] font-black transition ${on ? "bg-[#0A2540] text-[#FACC15]" : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"}`}
                    data-testid={`schedule-${service}-day-${d.i}`}
                    aria-pressed={on}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-[10px] text-[#64748B] italic">
            Ce service sera masqué automatiquement pour les clients entre <span className="font-bold text-[#0A2540]">{start}</span> et <span className="font-bold text-[#0A2540]">{end}</span> ({start > end ? "traverse minuit" : "même journée"}), aux jours cochés (fuseau Douala).
          </p>
        </div>
      )}
    </div>
  );
}
